import "@/App.css";
import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import UpdatePrompt from "@/components/UpdatePrompt";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Workspace from "@/pages/Workspace";
import RetailerSearchPage from "@/pages/RetailerSearchPage";
import PricingPage from "@/pages/PricingPage";
import AdminAnalytics from "@/pages/AdminAnalytics";
import { isStandalone } from "@/lib/pwa";
import { captureRefFromUrl } from "@/lib/referral";
import { injectSkimlinksTracker } from "@/lib/affiliateEngine";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white" data-testid="app-loading">
      <p className="text-sm font-bold uppercase tracking-[0.2em] text-black">Loading…</p>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

// Guest Mode — /app is never a hard login wall. If nobody is signed in, silently
// spin up an anonymous guest session (existing /api/auth/guest) so visitors land
// straight in Scroll/Discover/Check. Only Save/Vault, Daily Recs & Notifications
// prompt a real sign-in (handled inside Workspace), never this route itself.
function AppRoute({ children }) {
  const { user, loading, guestLogin } = useAuth();
  const [guestBusy, setGuestBusy] = useState(false);
  const [guestFailed, setGuestFailed] = useState(false);
  const attempted = useRef(false);

  useEffect(() => {
    if (loading || user || attempted.current) return;
    attempted.current = true;
    setGuestBusy(true);
    guestLogin().catch(() => setGuestFailed(true)).finally(() => setGuestBusy(false));
  }, [loading, user, guestLogin]);

  if (loading || guestBusy) return <Loading />;
  if (!user) return guestFailed ? <Navigate to="/" replace /> : <Loading />;
  return children;
}

// Owner-only route: strict is_admin check (others → /app). Mobile web is first-class —
// no PWA install gate here or anywhere else in the app.
function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  // Admin dashboard is WEBSITE-ONLY — never on the installed phone app.
  if (isStandalone()) return <Navigate to="/app" replace />;
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/" replace />;
  if (!user.is_admin) return <Navigate to="/app" replace />;
  return children;
}

function Entry() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  // Only a REAL signed-in user skips the marketing page — a guest session (silently
  // minted by AppRoute on /app) must never make the rebuilt Landing page unreachable.
  if (user && !user.is_guest) return <Navigate to="/app" replace />;
  // Standalone PWA bypasses the homepage and drops straight into login.
  if (isStandalone()) return <Navigate to="/login" replace />;
  return <Landing />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Entry />} />
      <Route path="/login" element={<Login />} />
      <Route path="/app" element={<AppRoute><Workspace /></AppRoute>} />
      <Route path="/search/:retailerId" element={<AppRoute><RetailerSearchPage /></AppRoute>} />
      <Route path="/pricing" element={<ProtectedRoute><PricingPage /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminAnalytics /></AdminRoute>} />
      <Route path="/analytics" element={<AdminRoute><AdminAnalytics /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  // Section 7 — capture ?ref=CODE (if present) once on load, survives the Google OAuth
  // round-trip in localStorage since that's same-origin on our own domain.
  useEffect(() => { captureRefFromUrl(); }, []);
  // Affiliate Engine — Section 2: global Skimlinks fallback script, loaded once app-wide.
  useEffect(() => { injectSkimlinksTracker(); }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <UpdatePrompt />
        <AppRouter />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
